import type { Source } from '@epimodev/callbag-doki';
import pipeSource from '@epimodev/callbag-doki/utils/pipe';
import map from '@epimodev/callbag-doki/operators/map';
import catchError from '@epimodev/callbag-doki/operators/catchError';
import httpRequest from '@epimodev/callbag-doki/sources/httpRequest';
import of from '@epimodev/callbag-doki/sources/of';

import type { ConfigContextState } from 'app/common/ConfigContext';
import { getMetricsSuffix } from 'app/config/metrics.config.helper';
import { websocket } from 'app/utils/websocket';
import { getBackendUrl, getVideogameUrl } from 'app/utils/api_url';
import { assertNever } from 'app/utils/types';

// FETCH URL
const getGameFetchUrl = (videogame: VideogameSlug, gameId: number, config: ConfigContextState) =>
  `${getBackendUrl()}/api/${getVideogameUrl(videogame)}/games/${gameId}${getMetricsSuffix(config)}`;

// API HELPERS
const mergeMatchLiveData = (
  matchData: NameBlockMatch,
  message: LiveMatchWSMessage
): NameBlockMatch => {
  switch (message.event_type) {
    case 'scoreboard': {
      const scoreboard = message.model;
      const games: typeof matchData.games = matchData.games.map(game => {
        const scoreboardGame = scoreboard.games.find(
          scoreboardGame => game.id === scoreboardGame.id
        );
        if (scoreboardGame) {
          const opponents = game.opponents.length
            ? game.opponents
            : matchData.opponents.map(matchOpponent => ({ id: matchOpponent.id }));

          const updatedOpponents = opponents.map(opponent => {
            const scoreboardTeams = scoreboardGame.teams ?? scoreboardGame.opponents;
            const scoreboardOpponent = scoreboardTeams?.find(
              scoreboardOpponent => opponent.id === scoreboardOpponent.id
            );
            if (scoreboardOpponent) {
              const updatedOpponent: Record<string, any> = {
                ...opponent
              };
              if ('side' in scoreboardOpponent) updatedOpponent.side = scoreboardOpponent.side;
              if ('kills' in scoreboardOpponent) updatedOpponent.kills = scoreboardOpponent.kills;
              if ('towers' in scoreboardOpponent)
                updatedOpponent.towers = scoreboardOpponent.towers;
              if ('round_score' in scoreboardOpponent)
                updatedOpponent.score = scoreboardOpponent.round_score;
              if ('heroes' in scoreboardOpponent)
                updatedOpponent.heroes = scoreboardOpponent.heroes;
              if ('drakes' in scoreboardOpponent) {
                const lolOpponent = scoreboardOpponent as ScoreboardTeamLol;
                return {
                  ...updatedOpponent,
                  drakes: lolOpponent.drakes,
                  inhibitors: lolOpponent.inhibitors,
                  nashors: lolOpponent.nashors
                };
              }
              if ('tower_status' in scoreboardOpponent)
                updatedOpponent.tower_status = scoreboardOpponent.tower_status;
              if ('score' in scoreboardOpponent) updatedOpponent.score = scoreboardOpponent.score;
              if ('halves' in scoreboardOpponent)
                updatedOpponent.halves = scoreboardOpponent.halves;
              if ('quarters' in scoreboardOpponent)
                updatedOpponent.quarters = scoreboardOpponent.quarters;
              if ('periods' in scoreboardOpponent)
                updatedOpponent.periods = scoreboardOpponent.periods;
              if ('sets' in scoreboardOpponent) updatedOpponent.sets = scoreboardOpponent.sets;
              return updatedOpponent;
            }
            return opponent;
          });

          const updatedGame = {
            ...game,
            status: scoreboardGame.status,
            winner_id: scoreboardGame.winner_id
          };
          if (game.videogame_slug === 'e-soccer') {
            updatedGame.opponents = updatedOpponents as ESoccerGameOpponent[];
            updatedGame.timer = scoreboardGame.timer ?? null;
            updatedGame.current_half = scoreboardGame.current_half ?? null;
          }
          if (game.videogame_slug === 'e-basketball') {
            updatedGame.opponents = updatedOpponents as EBasketballGameOpponent[];
            updatedGame.timer = scoreboardGame.timer ?? null;
            updatedGame.current_quarter = scoreboardGame.current_quarter ?? null;
          }
          if (game.videogame_slug === 'e-hockey') {
            updatedGame.opponents = updatedOpponents as EHockeyGameOpponent[];
            updatedGame.timer = scoreboardGame.timer ?? null;
            updatedGame.current_period = scoreboardGame.current_period ?? null;
          }
          if (game.videogame_slug === 'e-tennis') {
            updatedGame.opponents = updatedOpponents as ETennisGameOpponent[];
            updatedGame.current_game = scoreboardGame.current_game ?? null;
            updatedGame.current_game_score = scoreboardGame.current_game_score ?? null;
            updatedGame.server_id = scoreboardGame.server_id ?? null;
          }
          if (game.videogame_slug === 'cs-go') {
            updatedGame.opponents = updatedOpponents as GameOpponentCs[];
          }
          if (game.videogame_slug === 'dota-2') {
            updatedGame.opponents = updatedOpponents as LiveGameOpponentDota2[];
            updatedGame.radiant_gold_adv = scoreboardGame.radiant_gold_adv;
            updatedGame.timer = scoreboardGame.timer ?? {
              timer: null,
              paused: false,
              issued_at: '',
              type: 'snapshot'
            };
          }
          if (game.videogame_slug === 'league-of-legends') {
            updatedGame.opponents = updatedOpponents as GameOpponentLol[];
            updatedGame.timer = scoreboardGame.timer ?? {
              timer: null,
              paused: false,
              issued_at: '',
              type: 'snapshot'
            };
          }
          return updatedGame;
        }
        return game;
      });
      return {
        ...matchData,
        match_status: scoreboard.match_status,
        games
      };
    }

    case 'match_lifecycle': {
      return {
        ...matchData,
        match_status: message.model.match_status,
        forfeit: message.model.forfeit
      };
    }

    case 'game_lifecycle': {
      const games = matchData.games.map(game => {
        if (game.id === message.model.id) {
          return {
            ...game,
            status: message.model.status,
            winner_id: message.model.winner_id
          };
        }
        return game;
      });
      return {
        ...matchData,
        games
      };
    }

    default:
      assertNever(message);
      return matchData;
  }
};

// API CALLS
// Identity is the customer path segment (what REST sends as user_path_param),
// never the ?user= theme. webdriver is a headless-detection signal only.
const isWebdriver = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.webdriver);

const liveMatch = (
  url: string,
  matchId: number,
  customer: string | undefined
): Source<LiveMatchWSMessage> =>
  pipeSource(
    websocket<{ message: LiveMatchWSMessage }>(
      url,
      customer === 'pariru' ? `aggregated:match:${matchId}` : `v2:match:${matchId}`,
      'any',
      {
        params: { customer: customer ?? '', webdriver: isWebdriver() }
      }
    ),
    map(({ message }) => message)
  );

// Aggregated live feed (new backend, grad widget only). Same envelope as the
// v2 feed, different topic. Every `scoreboard` message is a full snapshot, so
// merging is a straight replace; lifecycle messages patch status/winner only.
const liveAggregatedMatch = (
  url: string,
  matchId: number,
  customer: string | undefined
): Source<AggregatedWSMessage> =>
  pipeSource(
    websocket<{ message: AggregatedWSMessage }>(url, `aggregated:match:${matchId}`, 'any', {
      params: { customer: customer ?? '', webdriver: isWebdriver() }
    }),
    map(({ message }) => message)
  );

// Aggregated REST snapshot -> a synthetic `scoreboard` message, so the same
// scan(mergeAggregatedMatchData) folds it before WS patches. A failing/absent
// snapshot (e.g. feed not deployed -> 404) is swallowed so the WS still runs.
const aggregatedMatchSnapshot = (url: string): Source<AggregatedWSMessage> =>
  pipeSource(
    httpRequest<AggregatedMatch>({ url }),
    map(
      response =>
        ({ event_type: 'scoreboard', model: response.data } as AggregatedScoreboardWSMessage)
    ),
    catchError(() => of<AggregatedWSMessage>())
  );

const mergeAggregatedMatchData = (
  matchData: AggregatedMatch | null,
  message: AggregatedWSMessage
): AggregatedMatch | null => {
  switch (message.event_type) {
    case 'scoreboard':
      return message.model;
    case 'match_lifecycle':
      return matchData ? { ...matchData, match_status: message.model.match_status } : matchData;
    case 'game_lifecycle':
      return matchData
        ? {
            ...matchData,
            games: matchData.games.map(game =>
              game.id === message.model.id
                ? { ...game, status: message.model.status, winner_id: message.model.winner_id }
                : game
            )
          }
        : matchData;
    default:
      assertNever(message);
      return matchData;
  }
};

const liveMicrobetting = (url: string, matchId: number, jwt: string): Source<MicroMarketsFeed> =>
  websocket<MicroMarketsFeed>(
    url,
    `match:${matchId}`,
    'notify',
    { params: { jwt } },
    'get_micromarkets'
  );

export {
  liveMatch,
  liveAggregatedMatch,
  aggregatedMatchSnapshot,
  getGameFetchUrl,
  mergeMatchLiveData,
  mergeAggregatedMatchData,
  liveMicrobetting
};
